/**
 * lib/ticketStatus.ts
 * Which statuses a ticket can move to from where it is now.
 *
 * Mirrors the API's state machine (sk-tickets-api
 * src/modules/tickets/status-guards.ts — identical on dev, stage and main on
 * 28 Oct 2026). The status menu used to offer all six statuses every time, so
 * a resolved ticket offered "open", the API answered 422 INVALID_TRANSITION,
 * and the agent saw only "Failed to update status" — a resolved ticket could
 * not be re-opened from the screen at all.
 *
 * An unknown current status (an older or newer API value) falls back to the
 * full list: the API still guards the move, and the menu never goes empty.
 */

export const ALL_STATUSES = [
  'open',
  'in_progress',
  'pending',
  'on_hold',
  'resolved',
  'closed',
] as const;

const ALLOWED: Record<string, readonly string[]> = {
  open: ['in_progress', 'pending', 'on_hold', 'resolved', 'closed'],
  in_progress: ['open', 'pending', 'on_hold', 'resolved', 'closed'],
  pending: ['in_progress', 'on_hold', 'resolved', 'closed'],
  on_hold: ['in_progress', 'pending', 'resolved', 'closed'],
  resolved: ['reopened', 'closed'],
  closed: ['reopened'],
  reopened: ['in_progress', 'pending', 'on_hold', 'resolved', 'closed'],
};

/** The statuses the menu should offer for a ticket that is `current` now. */
export function nextStatuses(
  current: string | null | undefined,
): readonly string[] {
  if (current && ALLOWED[current]) {
    return ALLOWED[current];
  }
  return ALL_STATUSES;
}

/** Menu / chip text for an API status: `in_progress` → "in progress", `reopened` → "re-open". */
export function statusMenuLabel(status: string): string {
  if (status === 'reopened') {
    return 're-open';
  }
  return status.replace(/_/g, ' ');
}
